import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './CableCard.css';

/* ─── Connector options ─── */
const GHS_OPTIONS = Array.from({ length: 10 }, (_, i) => 'GHS-' + (i + 2) + 'P');
const SH_OPTIONS  = Array.from({ length: 10 }, (_, i) => 'SH-'  + (i + 2) + 'P');

const PIN_COUNT = {};
GHS_OPTIONS.forEach((o) => { PIN_COUNT[o] = parseInt(o.replace('GHS-', '').replace('P', '')); });
SH_OPTIONS.forEach((o)  => { PIN_COUNT[o] = parseInt(o.replace('SH-',  '').replace('P', '')); });
PIN_COUNT['open-ended'] = 0;

const DEFAULT_WIRE_COLORS = [
  '#e60000', '#111111', '#111111', '#111111',
  '#111111', '#111111', '#111111', '#111111',
  '#111111', '#111111', '#111111', '#111111',
];

const COLOR_NAMES = {
  '#e60000':'Red',    '#ff3333':'Red',    '#ff0000':'Red',
  '#ffffff':'White',  '#111111':'Black',  '#000000':'Black',
  '#e6b800':'Yellow', '#ffcc00':'Yellow', '#ffff00':'Yellow',
  '#0073e6':'Blue',   '#0099ff':'Blue',   '#0000ff':'Blue',
  '#00b359':'Green',  '#00ff88':'Green',  '#00ff00':'Lime Green',
  '#e65c00':'Orange', '#ff6b00':'Orange', '#ff6600':'Orange',
  '#8000cc':'Violet', '#cc44ff':'Violet', '#7b2fff':'Purple',
  '#cc0066':'Pink',   '#ff88aa':'Pink',   '#ff1493':'Deep Pink',
  '#009999':'Teal',   '#88ffdd':'Cyan',   '#00ced1':'Teal',
  '#ff5500':'Amber',  '#00ccff':'Sky Blue','#006666':'Dark Teal',
  '#8B4513':'Brown',  '#D2691E':'Chocolate','#888888':'Gray',
  '#444444':'Dark Gray','#222222':'Charcoal',
  '#dc143c':'Crimson','#32cd32':'Lime Green',
};
function getColorName(hex) {
  if (!hex) return '';
  return COLOR_NAMES[hex.toLowerCase()] || hex.toUpperCase();
}
function darkenColor(hex) {
  if (!hex || !hex.startsWith('#')) return '#333';
  const r = Math.floor(parseInt(hex.slice(1, 3), 16) * 0.6);
  const g = Math.floor(parseInt(hex.slice(3, 5), 16) * 0.6);
  const b = Math.floor(parseInt(hex.slice(5, 7), 16) * 0.6);
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

/* ─────────────────────────────────────────
   Color Palette — rendered in a Portal so it
   always floats above ALL cards/cards/cards
───────────────────────────────────────── */
const PALETTE_COLORS = [
  '#e60000','#e65c00','#e6b800','#00b359',
  '#00ced1','#0073e6','#8000cc','#cc0066',
  '#ff88aa','#ffffff','#cccccc','#888888',
  '#444444','#111111','#8B4513','#D2691E',
  '#dc143c','#ff1493','#009999','#32cd32',
  '#ff6600','#0099ff','#cc44ff','#ffff00',
];

const ColorPalette = ({ currentColor, onSelect, onClose, anchorEl }) => {
  const ref = useRef(null);

  /* Position relative to the anchor wire-object element */
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const PAL_W = 204;
    const PAL_H = 220;
    let left = r.left;
    let top  = r.bottom + 4 + window.scrollY;
    // Keep inside viewport horizontally
    if (left + PAL_W > window.innerWidth - 8) left = window.innerWidth - PAL_W - 8;
    if (left < 8) left = 8;
    // Flip upward if overflows bottom
    if (r.bottom + PAL_H + 4 > window.innerHeight)
      top = r.top - PAL_H - 4 + window.scrollY;
    setPos({ top, left });
  }, [anchorEl]);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorEl, onClose]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="color-palette"
      style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 99999 }}
    >
      <div className="palette-header">
        <span>Wire Color</span>
        <button className="palette-close" onClick={onClose}>&#10005;</button>
      </div>
      <div className="palette-grid">
        {PALETTE_COLORS.map((c) => (
          <button key={c}
            className={'palette-swatch' + (currentColor === c ? ' active' : '')}
            style={{ background: c, border: c === '#ffffff' ? '1px solid #ccc' : 'none' }}
            onClick={() => onSelect(c)} title={getColorName(c)} />
        ))}
      </div>
      <div className="palette-custom">
        <label>Custom:</label>
        <input type="color" value={currentColor || '#ffffff'}
          onChange={(e) => onSelect(e.target.value)} className="palette-color-input" />
      </div>
    </div>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════════════
   JST CONNECTOR — TOP VIEW (from-side) and BOTTOM VIEW (to-side)

   Connectors are drawn as plan views (looking straight down/up).
   The long axis of the connector body runs VERTICALLY in the SVG
   (pins stacked top-to-bottom). Wires exit horizontally.

   FROM connector (left)  → TOP VIEW:
     You see the top face of the housing.
     GH: big latch arm panel covers most of top surface.
     SH: smooth flat top with small retention nub.
     Pin numbers on the center-facing edge.
     No pin cavities visible (they face down toward PCB).

   TO connector (right) → BOTTOM VIEW:
     You see the bottom face — open slots with crimp terminals inside.
     Each slot shows a long gold crimp pin barrel inside the cavity.
     No latch visible. Pin numbers on center-facing edge.

   Wires connect to the CENTER-FACING edge of each connector.
   LEFT  connector: center-facing edge = RIGHT edge (x = bX + BODY_W)
   RIGHT connector: center-facing edge = LEFT  edge (x = bX)
═══════════════════════════════════════════════════════════════════ */

/* Width of connector body in SVG X units — used for wire alignment */
const CONN_BODY_W_GH = 50;
const CONN_BODY_W_SH = 44;

const JSTConnector = ({
  side, view, rows, pinY, PIN_GAP, SVG_W, series,
  wireColors, wirePinFrom, wirePinTo,
}) => {
  const isGH      = series === 'GH';
  const isTopView = view === 'top';   // from-side = top; to-side = bottom

  /* ── Body geometry ── */
  const PAD_V  = isGH ? 12 : 8;       // space above pin-1 and below last pin
  const BODY_H = (rows - 1) * PIN_GAP + PAD_V * 2;
  const BODY_Y = pinY(0) - PAD_V;
  const BODY_W = isGH ? CONN_BODY_W_GH : CONN_BODY_W_SH;
  const R      = 3;                   // corner radius

  /* ── Colors ── */
  const IVORY    = isGH ? '#e6e2d8' : '#eceae4';
  const IV_LIGHT = '#f5f3ee';
  const IV_MID   = isGH ? '#dedad0' : '#e4e2dc';
  const IV_DARK  = isGH ? '#b8b5ae' : '#c4c1ba';
  const OUTLINE  = '#7a7870';

  /* Latch arm (top-view GH only) */
  const LATCH_INSET = 5;             // inset from edges of body
  const LATCH_PAD   = isGH ? 12 : 0; // extra horizontal depth of latch zone from wire-exit edge

  /* Crimp pin (bottom-view only) */
  const SLOT_W   = PIN_GAP - 10;      // slot width (horizontal) per pin
  const SLOT_H   = isGH ? 16 : 13;   // slot height = depth of pin cavity visible
  const GOLD_C   = '#d4980a';
  const GOLD_L   = '#f0c040';
  const METAL    = '#b0b0ac';
  const METAL_L  = '#d8d8d4';

  /* X anchor */
  const bX     = side === 'left' ? 0 : SVG_W - BODY_W;
  /* Center-facing edge (where wires attach) */
  const wireX  = side === 'left' ? bX + BODY_W : bX;
  /* Far edge (away from center) */
  const farX   = side === 'left' ? bX : bX + BODY_W;

  const uid2 = `jst-${series}-${side}`;

  /* Wire color lookup for this connector's pin slots */
  const getPinColor = (pinIdx) => {
    if (!wireColors) return null;
    for (let w = 0; w < wireColors.length; w++) {
      const from = parseInt(wirePinFrom?.[w]);
      const to   = parseInt(wirePinTo?.[w]);
      if (side === 'left'  && from === pinIdx + 1) return wireColors[w];
      if (side === 'right' && to   === pinIdx + 1) return wireColors[w];
    }
    return null;
  };

  return (
    <g>
      <defs>
        {/* Body gradient — light on center-facing edge, darker on far edge */}
        <linearGradient
          id={uid2+'-bg'}
          x1={side === 'left' ? '0%' : '100%'} y1="0%"
          x2={side === 'left' ? '100%'   : '0%'} y2="0%"
        >
          <stop offset="0%"   stopColor={IV_LIGHT} />
          <stop offset="80%"  stopColor={IVORY} />
          <stop offset="100%" stopColor={IV_DARK} />
        </linearGradient>

        <linearGradient
          id={uid2+'-sh'}
          x1={side === 'left' ? '0%' : '100%'} y1="0%"
          x2={side === 'left' ? '100%'   : '0%'} y2="0%"
        >
          <stop offset="0%"   stopColor={IVORY} />
          <stop offset="100%" stopColor={IV_DARK} />
        </linearGradient>

        {/* Latch gradient — top-to-bottom sheen */}
        <linearGradient id={uid2+'-la'} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={IV_LIGHT} />
          <stop offset="50%"  stopColor={IV_MID} />
          <stop offset="100%" stopColor={IV_DARK} />
        </linearGradient>

        {/* Gold pin gradient */}
        <linearGradient id={uid2+'-gd'} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={GOLD_L} />
          <stop offset="50%"  stopColor={GOLD_C} />
          <stop offset="100%" stopColor="#a06800" />
        </linearGradient>

        {/* Metal pin gradient */}
        <linearGradient id={uid2+'-mt'} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={METAL_L} />
          <stop offset="100%" stopColor={METAL} />
        </linearGradient>
      </defs>

      {/* ── Drop shadow ── */}
      <rect x={bX+2} y={BODY_Y+4} width={BODY_W} height={BODY_H }
        rx={R} fill="rgba(0,0,0,0.06)" />

      {/* ── Main body ── */}
      <rect x={bX} y={BODY_Y} width={BODY_W} height={BODY_H}
        rx={2} fill={`url(#${uid2}-bg)`} stroke="#9d9c94" strokeWidth={0.3} />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          BOTTOM VIEW rendering (FROM connector, left side)
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {!isTopView && (
        <g>
          {isGH ? (
            /* GH BOTTOM VIEW: large latch arm panel covering most of surface */
            <g>

              {/* Grip ridges across latch (horizontal lines = finger texture) */}
              {Array.from({ length: rows }, (_, ri) => {
                const gy = BODY_Y + PAD_V + ri * PIN_GAP * 1.00;
                return (
                  <g>
                  <line key={ri}
                    x1={bX + BODY_W * 0.5} y1={gy}
                    x2={bX + BODY_W * 0.95} y2={gy}
                    stroke="#a09e99" strokeWidth={5.4} opacity={1.0}
                  />

                  <rect
                    x={bX + BODY_W * 0.55} y={gy - 1.9}
                    width={BODY_W * 0.30} height={3.8}
                    rx={1}
                    fill={`url(#${uid2}-gd)`}
                  />

                  </g>
                );
              })}

              <rect
                x={bX} y={BODY_Y}
                width={BODY_W} height={PAD_V * 0.6}
                rx={2} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX - BODY_W * 0.05} y={BODY_Y + PAD_V * 0.6}
                width={BODY_W * 0.275} height={PAD_V * 0.75}
                rx={0} fill={IV_MID} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.225} y={BODY_Y + PAD_V * 0.6}
                width={BODY_W * 0.225} height={PAD_V * 0.75}
                rx={0} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              {/* Shawdow */}
              <rect
                x={bX} y={BODY_Y + PAD_V * 0.6 + PAD_V * 0.75}
                width={BODY_W * 0.45} height={PAD_V * 0.5}
                rx={2} fill={IV_DARK} stroke={IV_DARK} strokeWidth={0.0}
                opacity={0.4}
              />

              <rect
                x={bX} y={BODY_Y + BODY_H - PAD_V * 0.6}
                width={BODY_W} height={PAD_V * 0.6}
                rx={2} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX - BODY_W * 0.05} y={BODY_Y + BODY_H - PAD_V * 0.6 - PAD_V * 0.75}
                width={BODY_W * 0.275} height={PAD_V * 0.75}
                rx={0} fill={IV_MID} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.225} y={BODY_Y + BODY_H - PAD_V * 0.6 - PAD_V * 0.75}
                width={BODY_W * 0.225} height={PAD_V * 0.75}
                rx={0} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              {/* <rect
                x={bX + BODY_W * 0.18} y={BODY_Y - 1.5}
                width={BODY_W * 0.64} height={1.4}
                rx={0} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              /> */}

              {/* Latch arm panel — inset from all sides, covers ~80% of body */}
              <rect
                x={bX  - 2}
                y={BODY_Y + BODY_H /2 - Math.max(BODY_H - PAD_V * 3.4, BODY_H * 0.6) * 0.5}
                width={BODY_W * 0.95}
                height={Math.max(BODY_H - PAD_V * 3.1, BODY_H * 0.64)}
                rx={5}
                fill={`url(#${uid2}-la)`}
                stroke={IV_DARK} strokeWidth={0.4} opacity={0.4}
              />

              {/* Sheen on latch */}
              <rect
                x={bX - 2}
                y={BODY_Y + BODY_H /2 - Math.max(BODY_H - PAD_V * 3.4, BODY_H * 0.6) * 0.5}
                width={BODY_W }
                height={Math.max(BODY_H - PAD_V * 3.4, BODY_H * 0.6)}
                rx={2.5}
                stroke={IV_DARK} strokeWidth={0.4}
                fill="#f3f2ec"
              />
              
              {/* Latch hook nub — on the center-facing end (where it clicks) */}
              <rect
                x={bX - 4.5}
                y={BODY_Y + BODY_H /2 - Math.max(BODY_H - PAD_V * 3.4, BODY_H * 0.6) * 0.5}
                width={10}
                height={Math.max(BODY_H - PAD_V * 3.4, BODY_H * 0.6)}
                rx={3.0}
                fill="#f2f2ee" stroke={IV_DARK} strokeWidth={0.5}
              />
            </g>
          ) : (
            /* SH BOTTOM VIEW: smooth flat surface, small central nub */
            <g>

              {/* Shawdow*/}
              
              <rect 
                x={bX + BODY_W * 0.10} y={BODY_Y} width={BODY_W * 0.20} height={BODY_H}
                rx={0} fill={`url(#${uid2}-sh)`} stroke="#55544f" strokeWidth={0.0} 
              />

              {/* Flat center panel */}
              <rect
                x={bX} y={BODY_Y - 1.8}
                width={BODY_W * 0.20} height={BODY_H + 3.5}
                rx={1} fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.3}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.18} y={BODY_Y - 1.5}
                width={BODY_W * 0.68} height={1.5}
                rx={0} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.5}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.18} y={BODY_Y + BODY_H + 0.3}
                width={BODY_W * 0.68} height={1.5}
                rx={0} fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.5}
                opacity={1.0}
              />
              
              {/* Individual crimp pin slots — one per pin row */}
              {Array.from({ length: rows }, (_, i) => {
                const slotCenterY = BODY_Y + i * (PIN_GAP * 1.02) + SLOT_W * 3.0 * 0.5 - 1.5;//pinY(i);
                // Slot runs from near-center-face toward far edge
                const slotLeft    = side === 'left'
                  ? bX + 5
                  : bX + 5;
                const slotRight   = bX + BODY_W - 5;
                const slotActualW = slotRight - slotLeft;

                /* Partition depth: slot occupies full body width minus walls */
                return (
                  <g key={i}>
  
                  {/* crimp pin stopper*/}
                    <rect
                      x={bX + BODY_W * 0.18} y={slotCenterY - SLOT_W * 3.0 * 0.5}
                      width={BODY_W * 0.22} height={SLOT_W * 3.0}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.2}
                      opacity={1.0}
                    />

                    <rect
                      x={bX + BODY_W * 0.15} y={slotCenterY - SLOT_W * 2.9 * 0.5}
                      width={BODY_W * 0.22} height={SLOT_W * 2.8}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_LIGHT} strokeWidth={0.0} 
                      opacity={1.0}
                    />

                    <line key={i}
                      x1={bX + BODY_W * 0.20} y1={slotCenterY + SLOT_W * 3.0 * 0.5}
                      x2={bX + BODY_W * 0.20} y2={slotCenterY + SLOT_W * 3.0 * 0.5 + 6}
                      stroke={IV_DARK} strokeWidth={0.8} opacity={0.5}
                    />

                    <line key={i}
                      x1={bX + BODY_W * 0.20} y1={slotCenterY + SLOT_W * 3.0 * 0.5 + 0.6}
                      x2={bX + BODY_W * 0.38} y2={slotCenterY + SLOT_W * 3.0 * 0.5 + 0.6}
                      stroke={IV_DARK} strokeWidth={0.8} opacity={0.5}
                    />
                      
                  </g>
                );
              })}

              <rect
                x={bX + BODY_W * 0.18} y={BODY_Y + BODY_H + 1.5 - SLOT_W * 3.0}
                width={BODY_W * 0.22} height={SLOT_W * 3.0}
                rx={1}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.1}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.15} y={BODY_Y + BODY_H + 1.5 - SLOT_W * 3.0}
                width={BODY_W * 0.22} height={SLOT_W * 2.9}
                rx={1}
                fill={IV_LIGHT} stroke={IV_LIGHT} strokeWidth={0.0}
                opacity={1.0}
              />

            </g>
          )}

          {/* Pin number labels — on center-facing edge */}
          {Array.from({ length: rows }, (_, i) => (
            <text key={i}
              x={wireX + (side === 'left' ? 6 : -6)}
              y={pinY(i) - 3.0}
              textAnchor={side === 'left' ? 'start' : 'end'}
              fill="#707070" fontSize="6"
              fontFamily="Share Tech Mono, monospace"
            >
              {i + 1}
            </text>
          ))}
        </g>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          TOP VIEW rendering (TO connector, right side)
          Open slots showing crimp pin terminals inside
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isTopView && (
        <g>
          {isGH ? (
            /* GH BOTTOM VIEW: large latch arm panel covering most of surface */
            <g>
              {/* Slot background strip — slightly darker than body */}
              <rect
                x={bX + BODY_W * 0.06} y={BODY_Y + 6}
                width={BODY_W * 0.80} height={BODY_H - 12}
                rx={2} fill={IV_DARK} opacity={0.9}
              />

              <rect
                x={bX + BODY_W * 0.85} y={BODY_Y}
                width={BODY_W * 0.14} height={BODY_H}
                rx={2}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.0}
                opacity={1.0}
              />

              {/* Individual crimp pin slots — one per pin row */}
              {Array.from({ length: rows }, (_, i) => {
                const slotCenterY = BODY_Y + 12 + i * PIN_GAP ;//pinY(i);
                const slotTop     = slotCenterY - SLOT_W / 2;
                // Slot runs from near-center-face toward far edge
                const slotLeft    = side === 'left'
                  ? bX + 5
                  : bX + 5;
                const slotRight   = bX + BODY_W - 5;
                const slotActualW = slotRight - slotLeft;

                /* Partition depth: slot occupies full body width minus walls */
                return (
                  <g key={i}>
                    {/* Slot cavity (dark recess) */}
                    <rect
                      x={bX + BODY_W * 0.4} y={slotTop}
                      width={4} height={SLOT_W *2.2}
                      rx={2}
                      fill="#443e32" stroke="#373737" strokeWidth={0.5}
                    />

                    {/* Crimp pin barrel — long gold rectangle inside slot */}
                    {/* The pin runs along the full slot length */}
                    
                    <rect
                      x={bX + BODY_W * 0.4} y={slotTop + SLOT_W * 0.15}
                      width={4} height={SLOT_W * 1.8}
                      rx={2}
                      fill={`url(#${uid2}-gd)`}
                    />

                    <rect
                      x={bX + BODY_W * 0.1} y={slotTop + SLOT_W * 0.15}
                      width={BODY_W * 0.35} height={SLOT_W * 1.8}
                      rx={2}
                      fill={`url(#${uid2}-gd)`}
                    />

                  {/* crimp pin stopper*/}
                    <rect
                      x={bX + BODY_W * 0.50} y={slotCenterY - SLOT_W *2.2 * 0.5}
                      width={BODY_W * 0.35} height={SLOT_W * 3.0}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.5}
                      opacity={1.0}
                    />

                    <rect
                      x={bX + BODY_W * 0.34} y={slotCenterY + PIN_GAP/2 - 0.6}
                      width={BODY_W * 0.47} height={SLOT_W * 1.8}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.5}
                      opacity={0.7}
                    />

                    {/* shawdow */}
                    <rect
                      x={bX + BODY_W * 0.06} y={slotCenterY - PAD_V + 1.5}
                      width={BODY_W * 0.38 + 1} height={PAD_V * 0.8 + 1}
                      rx={3}
                      fill={IV_DARK} stroke={IV_MID} strokeWidth={0.0}
                      opacity={1.0}
                    />

                    {/* crimp pin slots */}
                    <rect
                      x={bX + BODY_W * 0.05} y={slotCenterY - PAD_V + 1.5}
                      width={BODY_W * 0.38} height={PAD_V * 0.8}
                      rx={3}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.0}
                      opacity={1.0}
                    />

                    
                  </g>
                );
              })}

              {/* shawdow */}
              <rect
                x={bX + BODY_W * 0.06} y={BODY_Y + BODY_H - PAD_V * 0.8 - 0.2}
                width={BODY_W * 0.38 + 1.0} height={PAD_V * 0.8}
                rx={3}
                fill={IV_DARK} stroke={IV_MID} strokeWidth={0.0}
                opacity={1.0}
              />

              <rect
              x={bX + BODY_W * 0.05} y={BODY_Y + BODY_H - PAD_V * 0.8 - 0.2}
              width={BODY_W * 0.38} height={PAD_V * 0.8}
              rx={3}
              fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.0}
              opacity={1.0}
              />

            </g>
          ) : (
            /* SH TOP VIEW: smooth flat surface, small central nub */
            <g>
              {/* Slot background strip — slightly darker than body */}
              <rect
                x={bX + BODY_W * 0.18} y={BODY_Y + 3}
                width={BODY_W * 0.80} height={BODY_H - 6}
                rx={2} fill={IV_DARK} opacity={0.9}
              />

              <rect
                x={bX} y={BODY_Y}
                width={BODY_W * 0.08} height={BODY_H}
                rx={1}
                fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.5}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.77} y={BODY_Y}
                width={BODY_W * 0.22} height={BODY_H}
                rx={2}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.0}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.15} y={BODY_Y - 2.0}
                width={BODY_W * 0.84} height={2.0}
                rx={0}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.3}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.65} y={BODY_Y - 2.0}
                width={BODY_W * 0.35} height={5.0}
                rx={1}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.78} y={BODY_Y - 2.0}
                width={BODY_W * 0.20} height={6.0}
                rx={1}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.0}
                opacity={1.0}
              />


              {/* Individual crimp pin slots — one per pin row */}
              {Array.from({ length: rows }, (_, i) => {
                const slotCenterY = BODY_Y + PAD_V + i * PIN_GAP ;//pinY(i);
                const SHslotCenterY = BODY_Y + PAD_V + i * PIN_GAP ;//pinY(i);
                const slotTop     = slotCenterY - SLOT_W / 2;
                // Slot runs from near-center-face toward far edge
                const slotLeft    = side === 'left'
                  ? bX + 5
                  : bX + 5;
                const slotRight   = bX + BODY_W - 5;
                const slotActualW = slotRight - slotLeft;

                /* Partition depth: slot occupies full body width minus walls */
                return (
                  <g key={i}>
                    {/* Slot cavity (dark recess) */}
                    <rect
                      x={bX + BODY_W * 0.32} y={SHslotCenterY - SLOT_W *2.2/2}
                      width={4} height={SLOT_W *2.2}
                      rx={2}
                      fill="#443e32" stroke="#373737" strokeWidth={0.5}
                    />

                    {/* Crimp pin barrel — long gold rectangle inside slot */}
                    {/* The pin runs along the full slot length */}
                    
                    <rect
                      x={bX + BODY_W * 0.32} y={SHslotCenterY - SLOT_W * 1.8/2}
                      width={4} height={SLOT_W * 1.8}
                      rx={2}
                      fill={`url(#${uid2}-gd)`}
                    />

                    <rect
                      x={bX + BODY_W * 0.2} y={SHslotCenterY - SLOT_W * 1.5/2}
                      width={BODY_W * 0.2} height={SLOT_W * 1.5}
                      rx={2}
                      fill={`url(#${uid2}-gd)`}
                    />

                  {/* crimp pin stopper*/}
                    <rect
                      x={bX + BODY_W * 0.42} y={SHslotCenterY - SLOT_W * 3.0/2}
                      width={BODY_W * 0.38} height={SLOT_W * 3.0}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.2}
                      opacity={1.0}
                    />

                    <rect
                      x={bX + BODY_W * 0.25} y={SHslotCenterY + PIN_GAP/2 - SLOT_W * 1.6/2}
                      width={BODY_W * 0.47} height={SLOT_W * 1.6}
                      rx={1}
                      fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.5}
                      opacity={0.7}
                    />

                    

                    {/* crimp pin slots */}
                    {i !== rows - 1 && (
                      <g>

                      {/* shawdow */}
                      <rect
                        x={bX + BODY_W * 0.19} y={SHslotCenterY + PIN_GAP/2 - PAD_V * 0.9/2}
                        width={BODY_W * 0.10 + 1} height={PAD_V * 0.9 + 1}
                        rx={0}
                        fill={IV_DARK} stroke={IV_DARK} strokeWidth={0.0}
                        opacity={0.4}
                      />

                      <rect
                        x={bX + BODY_W * 0.17} y={SHslotCenterY + PIN_GAP/2 - PAD_V * 0.9/2}
                        width={BODY_W * 0.11} height={PAD_V * 0.9}
                        rx={0}
                        fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.0}
                        opacity={1.0}
                      />

                      </g>
                    )}
                    
                  </g>
                );
              })}

              {/* shawdow */}
              <rect
                x={bX + BODY_W * 0.19} y={BODY_Y + 0.3}
                width={BODY_W * 0.10 + 1} height={PAD_V * 0.7}
                rx={0}
                fill={IV_DARK} stroke={IV_MID} strokeWidth={0.0}
                opacity={1.0}
              />

              <rect
              x={bX + BODY_W * 0.18} y={BODY_Y + 0.3}
              width={BODY_W * 0.10} height={PAD_V * 0.7}
              rx={0}
              fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.0}
              opacity={1.0}
              />

              {/* shawdow */}
              <rect
                x={bX + BODY_W * 0.19} y={BODY_Y + BODY_H - PAD_V * 0.7 - 0.2}
                width={BODY_W * 0.10 + 1} height={PAD_V * 0.7}
                rx={0}
                fill={IV_DARK} stroke={IV_MID} strokeWidth={0.0}
                opacity={1.0}
              />

              <rect
              x={bX + BODY_W * 0.18} y={BODY_Y + BODY_H - PAD_V * 0.7 - 0.2}
              width={BODY_W * 0.10} height={PAD_V * 0.7}
              rx={0}
              fill={IV_LIGHT} stroke={IV_MID} strokeWidth={0.0}
              opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.15} y={BODY_Y + BODY_H}
                width={BODY_W * 0.84} height={2.0}
                rx={0}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.2}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.65} y={BODY_Y + BODY_H - 3.0}
                width={BODY_W * 0.35} height={5.0}
                rx={1}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.4}
                opacity={1.0}
              />

              <rect
                x={bX + BODY_W * 0.78} y={BODY_Y + BODY_H - 5.0}
                width={BODY_W * 0.20} height={6.0}
                rx={1}
                fill={IV_LIGHT} stroke={IV_DARK} strokeWidth={0.0}
                opacity={1.0}
              />

            </g>
          )}

          {/* Pin number labels — center-facing side */}
          {Array.from({ length: rows }, (_, i) => (
            <text key={i}
              x={wireX + (side === 'left' ? 3 : -3)}
              y={pinY(i) + - 3.0}
              textAnchor={side === 'left' ? 'start' : 'end'}
              fill="#555" fontSize="6"
              fontFamily="Share Tech Mono, monospace"
            >
              {i + 1}
            </text>
          ))}
        </g>
      )}

      {/* ── Connector label below housing ── */}
      <text
        x={bX + BODY_W / 2}
        y={BODY_Y + BODY_H + 12}
        textAnchor="middle"
        fill="#666" fontSize="7"
        fontFamily="Share Tech Mono, monospace"
      >
        {`${series === 'GH' ? 'GHR' : 'SHR'}-${String(rows).padStart(2,'0')}V-S${series === 'SH' ? '-B' : ''}`}
      </text>
      {/* View label */}
      {/* <text
        x={bX + BODY_W / 2}
        y={BODY_Y - 6}
        textAnchor="middle"
        fill="#999" fontSize="5.5"
        fontFamily="Share Tech Mono, monospace"
      >
        {isTopView ? '▲ TOP VIEW' : '▼ BOTTOM VIEW'}
      </text> */}
    </g>
  );
};

/* ─────────────────────────────────────────
   Cable Diagram
───────────────────────────────────────── */
const CableDiagram = ({
  fromConnector, toConnector,
  numCables, cableLength, noteText,
  wireColors, wirePinFrom, wirePinTo,
  cardIndex,
}) => {
  if (!fromConnector && !toConnector) {
    return (
      <div className="diagram-placeholder">
        <span className="placeholder-icon">&#9135;</span>
        <span>Select connectors to preview cable assembly</span>
      </div>
    );
  }
  const totalWires = wireColors.length;
  if (totalWires === 0) {
    return (
      <div className="diagram-placeholder">
        <span className="placeholder-icon">&#9135;</span>
        <span>Configure connectors with pins to see diagram</span>
      </div>
    );
  }

  /* Layout */
  const WIRE_THICK = 3.5;
  const PIN_GAP    = 12;
  const PAD_TOP    = 14;
  const PAD_BOT    = 8;
  const CONN_W     = CONN_BODY_W_SH;
  const SVG_W      = 600;

  const isOpenLeft  = fromConnector === 'open-ended';
  const isOpenRight = toConnector   === 'open-ended';

  const rowsLeft  = isOpenLeft  ? 0 : (PIN_COUNT[fromConnector] || 0);
  const rowsRight = isOpenRight ? 0 : (PIN_COUNT[toConnector]   || 0);
  const maxRows   = Math.max(rowsLeft, rowsRight, totalWires);

  const SVG_H = PAD_TOP + (maxRows - 1) * PIN_GAP + PAD_BOT + 16;
  const pinY  = (i) => PAD_TOP + i * PIN_GAP;

  // X where wires start/end
  const lWireStartX = isOpenLeft  ? 0       : CONN_W;
  const rWireEndX   = isOpenRight ? SVG_W*0.95   : SVG_W - CONN_W;

  const FLOAT_REACH = 0.90;
  const totalSpan   = rWireEndX - lWireStartX;

  const uid = 'cd' + cardIndex;

  /* Wire paths */
  const wirePaths = wireColors.map((color, i) => {
    const rawFrom = parseInt(wirePinFrom[i]);
    const rawTo   = parseInt(wirePinTo[i]);
    const fromVal = isNaN(rawFrom) ? (i + 1) : rawFrom;
    const toVal   = isNaN(rawTo)   ? (i + 1) : rawTo;
    if (fromVal === 0 && toVal === 0) return null;

    let y1;
    if (isOpenLeft)       y1 = pinY(i);
    else if (fromVal === 0) y1 = pinY(i );
    else                  y1 = pinY(Math.min(fromVal, rowsLeft || 1) - 1);

    let y2;
    if (isOpenRight)      y2 = pinY(i);
    else if (toVal === 0)   y2 = pinY(i );
    else                  y2 = pinY(Math.min(toVal, rowsRight || 1) - 1);

    const floatingLeft  = !isOpenLeft  && fromVal === 0;
    const floatingRight = !isOpenRight && toVal   === 0;

    let x1 = lWireStartX;
    let x2 = rWireEndX;
    if (floatingLeft)  x1 = rWireEndX   - totalSpan * FLOAT_REACH;
    if (floatingRight) x2 = lWireStartX + totalSpan * FLOAT_REACH;

    let d;
    const curve = !isOpenLeft && !isOpenRight && y1 !== y2 && !floatingLeft && !floatingRight;
    if (curve) {
      const t = (x2 - x1) * 0.38;
      d = `M ${x1} ${y1} C ${x1+t} ${y1},${x2-t} ${y2},${x2} ${y2}`;
    } else {
      d = `M ${x1} ${y1} L ${x2} ${y2}`;
    }
    return { d, color, y1, y2, x1, x2, fromVal, toVal };
  }).filter(Boolean);

  return (
    <div className="cable-diagram-wrapper">

      

      {/* SVG */}
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        className="cable-diagram-svg"
        overflow="visible"
      >
        <defs>
          {/* Single shared subtle glow filter — applied to the whole wire group, not per-wire */}
          <filter id={uid + '-wf'} x="-8%" y="-40%" width="116%" height="180%">
            <feGaussianBlur stdDeviation="0.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* No per-wire gradients — gradients applied to fills, not strokes, so they don't work on paths anyway */}
        </defs>

        <text 
        x={SVG_W / 2} 
        y={8}
        fontSize="10" 
        textAnchor="middle" // Centers the text
        fill="#666"
        fontFamily="Share Tech Mono, monospace"
      >
          &#8596; Length = {cableLength} mm
      </text>


        {/* ── Wire shadow pass (all wires, consistent +2px thicker, no filter) ── */}
        <g opacity="0.22">
          {wirePaths.map(({ d }, i) => (
            <path key={i} d={d}
              stroke="#000000" strokeWidth={WIRE_THICK + 2}
              fill="none" strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </g>

        {/* ── Main wire pass — all same strokeWidth, no per-element filter ── */}
        <g filter={`url(#${uid}-wf)`}>
          {wirePaths.map(({ d, color }, i) => (
            <path key={i} d={d}
              stroke={color} strokeWidth={WIRE_THICK}
              fill="none" strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </g>

        {/* ── Highlight pass — thin bright stripe, same path, offset upward via dy attribute ── */}
        <g opacity="0.30">
          {wirePaths.map(({ d }, i) => (
            <path key={i} d={d}
              stroke="rgba(255,255,255,0.9)" strokeWidth={1.2}
              fill="none" strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </g>

        {/* ── Connectors on top of wires ── */}
        {/* Left = FROM connector = TOP VIEW; Right = TO connector = BOTTOM VIEW */}
        {!isOpenLeft && rowsLeft > 0 && (
          <JSTConnector
            side="left" view="top" rows={rowsLeft}
            pinY={pinY} PIN_GAP={PIN_GAP} SVG_W={SVG_W}
            series={fromConnector.startsWith('GHS') ? 'GH' : 'SH'}
            wireColors={wireColors} wirePinFrom={wirePinFrom} wirePinTo={wirePinTo}
          />
        )}
        {!isOpenRight && rowsRight > 0 && (
          <JSTConnector
            side="right" view="bottom" rows={rowsRight}
            pinY={pinY} PIN_GAP={PIN_GAP} SVG_W={SVG_W}
            series={toConnector.startsWith('GHS') ? 'GH' : 'SH'}
            wireColors={wireColors} wirePinFrom={wirePinFrom} wirePinTo={wirePinTo}
          />
        )}

      
      </svg>

      {/* Legend */}
      {/* <div className="wire-legend">
        {wireColors.map((c, i) => {
          const rawFrom = parseInt(wirePinFrom[i]);
          const rawTo   = parseInt(wirePinTo[i]);
          const fromVal = isNaN(rawFrom) ? (i + 1) : rawFrom;
          const toVal   = isNaN(rawTo)   ? (i + 1) : rawTo;
          if (fromVal === 0 && toVal === 0) return null;
          const isDark = c === '#111111' || c === '#000000';
          const fromLabel = isOpenLeft   ? 'open' : (fromVal === 0 ? 'float' : fromVal);
          const toLabel   = isOpenRight  ? 'open' : (toVal   === 0 ? 'float' : toVal);
          return (
            <div key={i} className="legend-item">
              <div className="legend-swatch" style={{
                background: c,
                // border: isDark ? '1.5px solid #666' : '1px solid rgba(0,0,0,0.12)',
              }} />
              <div className="legend-text">
                <span className="legend-pin">{fromLabel} &#8594; {toLabel}</span>
                <span className="legend-color-name" style={{
                  color: isDark ? '#444' : darkenColor(c), fontWeight: 700,
                }}>
                  {getColorName(c)}
                </span>
              </div>
            </div>
          );
        })}
      </div> */}

      {/* Note */}
      {noteText && noteText.trim() && (
        <div className="diagram-note">
          <span className="note-label">Note:</span>
          <span className="note-text"> {noteText}</span>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   CableCard
───────────────────────────────────────── */
const CableCard = ({ index, card, onUpdate }) => {
  const [openPaletteIdx, setOpenPaletteIdx] = useState(null);
  const [paletteAnchor,  setPaletteAnchor]  = useState(null);
  const wireRefs = useRef([]);

  const fromPins = card.fromConnector
    ? (card.fromConnector === 'open-ended' ? 0 : (PIN_COUNT[card.fromConnector] || 0)) : 0;
  const toPins = card.toConnector
    ? (card.toConnector === 'open-ended' ? 0 : (PIN_COUNT[card.toConnector] || 0)) : 0;
  const wireCount = Math.max(fromPins, toPins);

  /* Sync arrays */
  useEffect(() => {
    const prev     = card.wireColors  || [];
    const prevFrom = card.wirePinFrom || [];
    const prevTo   = card.wirePinTo   || [];
    if (wireCount !== prev.length || wireCount !== prevFrom.length || wireCount !== prevTo.length) {
      onUpdate(card.id, {
        wireColors:  Array.from({ length: wireCount }, (_, i) => prev[i]     || DEFAULT_WIRE_COLORS[i % DEFAULT_WIRE_COLORS.length]),
        wirePinFrom: Array.from({ length: wireCount }, (_, i) => prevFrom[i] !== undefined ? prevFrom[i] : (i + 1)),
        wirePinTo:   Array.from({ length: wireCount }, (_, i) => prevTo[i]   !== undefined ? prevTo[i]   : (i + 1)),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wireCount]);

  const handleWireClick = useCallback((i, e) => {
    if (openPaletteIdx === i) {
      setOpenPaletteIdx(null);
      setPaletteAnchor(null);
    } else {
      setOpenPaletteIdx(i);
      setPaletteAnchor(wireRefs.current[i] || e.currentTarget);
    }
  }, [openPaletteIdx]);

  const handleWireColor = useCallback((color) => {
    const c = [...card.wireColors]; c[openPaletteIdx] = color;
    onUpdate(card.id, { wireColors: c });
    setOpenPaletteIdx(null);
    setPaletteAnchor(null);
  }, [card.wireColors, card.id, onUpdate, openPaletteIdx]);

  const closePalette = useCallback(() => {
    setOpenPaletteIdx(null);
    setPaletteAnchor(null);
  }, []);

  const handlePinFrom = (idx, val) => {
    const raw = val === '' ? 0 : parseInt(val);
    const max = fromPins > 0 ? fromPins : wireCount;
    const v = isNaN(raw) ? 0 : (raw === 0 ? 0 : Math.min(Math.max(raw, 1), max || 1));
    const arr = [...(card.wirePinFrom || [])]; arr[idx] = v;
    onUpdate(card.id, { wirePinFrom: arr });
  };

  const handlePinTo = (idx, val) => {
    const raw = val === '' ? 0 : parseInt(val);
    const max = toPins > 0 ? toPins : wireCount;
    const v = isNaN(raw) ? 0 : (raw === 0 ? 0 : Math.min(Math.max(raw, 1), max || 1));
    const arr = [...(card.wirePinTo || [])]; arr[idx] = v;
    onUpdate(card.id, { wirePinTo: arr });
  };

  const wirePinFrom = card.wirePinFrom || (card.wireColors || []).map((_, i) => i + 1);
  const wirePinTo   = card.wirePinTo   || (card.wireColors || []).map((_, i) => i + 1);
  const maxFrom     = fromPins > 0 ? fromPins : wireCount;
  const maxTo       = toPins   > 0 ? toPins   : wireCount;

  return (
    <div className="cable-card">
      <div className="card-index-badge">
        <span className="card-index-num">CABLE TYPE {String(index + 1).padStart(2, '0')}</span>
      </div>

      <div className="card-body">
        {/* LEFT */}
        <div className="card-left no-print">
          <div className="card-left-fields">

            <div className="card-field">
              <label className="card-field-label">From Connector</label>
              <div className="card-select-wrap">
                <select value={card.fromConnector}
                  onChange={(e) => onUpdate(card.id, { fromConnector: e.target.value })}
                  className="card-select">
                  <option value="">- Select -</option>
                  <optgroup label="GHS Series (JST GH)">
                    {GHS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                  <optgroup label="SH Series (JST SH)">
                    {SH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                  <option value="open-ended">open-ended</option>
                </select>
                <span className="card-select-arrow">&#9662;</span>
              </div>
            </div>

            <div className="card-field">
              <label className="card-field-label">To Connector</label>
              <div className="card-select-wrap">
                <select value={card.toConnector}
                  onChange={(e) => onUpdate(card.id, { toConnector: e.target.value })}
                  className="card-select">
                  <option value="">- Select -</option>
                  <optgroup label="GHS Series (JST GH)">
                    {GHS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                  <optgroup label="SH Series (JST SH)">
                    {SH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                  <option value="open-ended">open-ended</option>
                </select>
                <span className="card-select-arrow">&#9662;</span>
              </div>
            </div>

            <div className="card-field">
              <label className="card-field-label">No. of Cables</label>
              <div className="card-select-wrap">
                <select value={card.numCables}
                  onChange={(e) => onUpdate(card.id, { numCables: parseInt(e.target.value) || 1 })}
                  className="card-select">
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <span className="card-select-arrow">&#9662;</span>
              </div>
            </div>

            <div className="card-field">
              <label className="card-field-label">Length of Cable</label>
              <div className="card-input-wrap">
                <input type="number" min="1" placeholder="e.g. 150"
                  value={card.cableLength || ''}
                  onChange={(e) => onUpdate(card.id, { cableLength: e.target.value })}
                  className="card-input" />
                <span className="card-input-unit">mm</span>
              </div>
            </div>

            <div className="card-field">
              <label className="card-field-label">Note</label>
              <textarea className="card-textarea" rows={3} maxLength={300}
                placeholder="Add any notes about this cable..."
                value={card.noteText || ''}
                onChange={(e) => onUpdate(card.id, { noteText: e.target.value })} />
            </div>

          </div>

          {/* Wire list */}
          {wireCount > 0 ? (
            <div className="wire-objects-section">
              <div className="wire-objects-label">
                <span>Wires</span>
                <span className="wire-hint">0 = floating / skip</span>
              </div>
              <div className="wire-row-header">
                <span className="pin-col-label">From</span>
                <span className="wire-col-label">Color</span>
                <span className="pin-col-label">To</span>
              </div>
              <div className="wire-objects-list">
                {card.wireColors.map((color, i) => {
                  const fromVal = wirePinFrom[i] !== undefined ? wirePinFrom[i] : (i + 1);
                  const toVal   = wirePinTo[i]   !== undefined ? wirePinTo[i]   : (i + 1);
                  const hidden  = fromVal === 0 && toVal === 0;
                  return (
                    <div key={i} className={'wire-object-row' + (hidden ? ' wire-row-hidden' : '')}>
                      <input type="number" className="pin-input"
                        min={0} max={maxFrom} value={fromVal}
                        onChange={(e) => handlePinFrom(i, e.target.value)}
                        style={{ borderColor: color + '88' }} />
                      <div
                        ref={el => wireRefs.current[i] = el}
                        className="wire-object"
                        style={{
                          background: `linear-gradient(90deg,${color}bb,${color} 25%,${color} 75%,${color}bb)`,
                          boxShadow: `0 0 5px ${color}44`,
                          borderColor: color + '55',
                          opacity: hidden ? 0.35 : 1,
                        }}
                        onClick={(e) => handleWireClick(i, e)}
                        title={'Wire ' + (i + 1) + ' — click to change color'}
                      />
                      <input type="number" className="pin-input"
                        min={0} max={maxTo} value={toVal}
                        onChange={(e) => handlePinTo(i, e.target.value)}
                        style={{ borderColor: color + '88' }} />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="wire-prompt">Select connectors to configure wires</div>
          )}

          {/* Portal-rendered palette */}
          {openPaletteIdx !== null && paletteAnchor && (
            <ColorPalette
              currentColor={card.wireColors[openPaletteIdx]}
              onSelect={handleWireColor}
              onClose={closePalette}
              anchorEl={paletteAnchor}
            />
          )}
        </div>

        {/* RIGHT */}
        <div className="card-right pdf-target">
          <div className="card-right-header">
            {/* <span className="card-right-title">Cable Type</span> */}
            <span className="card-index-num card-right-title">CABLE TYPE {String(index + 1).padStart(2, '0')}</span>
            {card.fromConnector && card.toConnector && (
              <span className="card-right-meta">
                {card.fromConnector} &#8594; {card.toConnector}
              </span>
            )}
            <span className="info-badge qty-badge">
              &#10005; Qty: <strong>{card.numCables || 1}</strong>
            </span>
          </div>
          <CableDiagram
            fromConnector={card.fromConnector}
            toConnector={card.toConnector}
            numCables={card.numCables}
            cableLength={card.cableLength}
            noteText={card.noteText}
            wireColors={card.wireColors}
            wirePinFrom={wirePinFrom}
            wirePinTo={wirePinTo}
            cardIndex={index}
          />
        </div>
      </div>
    </div>
  );
};

export default CableCard;
